import {NgTemplateOutlet} from "@angular/common"
import {Component, computed, DestroyRef, effect, inject, input, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink} from "@angular/router"
import {HintComponent, HintTypes} from "@app/common/components/hint/hint.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {JobsHelperStateUpdateGQL} from "@common/helpers/jobs/jobs.helper.generated"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {getSimpleJobState, SimpleJobState} from "@app/textures/utils/simple-job-state"
import {filter, interval} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-task-progress",
    standalone: true,
    templateUrl: "./job-task-progress.component.html",
    styleUrls: ["./job-task-progress.component.scss"],
    imports: [MatProgressBarModule, MatTooltipModule, RouterLink, NgTemplateOutlet, HintComponent],
})
export class JobTaskProgressComponent {
    readonly HintTypes = HintTypes
    readonly $jobId = input.required<string>({alias: "jobId"})
    readonly $label = input<string | undefined>(undefined, {alias: "label"})

    readonly jobCompleted = output<void>()
    readonly jobFailed = output<void>()

    private readonly destroyRef = inject(DestroyRef)
    private readonly jobsHelperStateUpdateGql = inject(JobsHelperStateUpdateGQL)
    readonly $can = inject(PermissionsService).$to

    readonly $jobState = signal<SimpleJobState | undefined>(undefined)
    readonly $progress = signal<number>(0)
    private fetching = false

    readonly $isProcessing = computed(() => this.$jobState() === SimpleJobState.InProgress)
    readonly $isFailed = computed(() => this.$jobState() === SimpleJobState.Failed)
    readonly $isCancelled = computed(() => this.$jobState() === SimpleJobState.Cancelled)
    private readonly $isTerminal = computed(() => {
        const state = this.$jobState()
        return state === SimpleJobState.Complete || state === SimpleJobState.Failed || state === SimpleJobState.Cancelled
    })

    constructor() {
        effect(() => {
            const id = this.$jobId()
            if (id) {
                void this.fetchJobState(id)
            }
        })

        interval(3000)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => !document.hidden && !this.fetching && !this.$isTerminal()),
            )
            .subscribe(() => {
                const id = this.$jobId()
                if (id) {
                    void this.fetchJobState(id)
                }
            })
    }

    private async fetchJobState(jobId: string) {
        if (this.fetching) {
            return
        }
        this.fetching = true
        try {
            const result = await fetchThrowingErrors(this.jobsHelperStateUpdateGql)({ids: [jobId]})
            const job = result.jobs.find((j) => j?.id === jobId)
            if (job) {
                const previousState = this.$jobState()
                const newState = getSimpleJobState(job.state)
                this.$jobState.set(newState)
                this.$progress.set(job.progress ?? 0)

                if (previousState === SimpleJobState.InProgress && newState === SimpleJobState.Complete) {
                    this.jobCompleted.emit()
                }
                if (previousState === SimpleJobState.InProgress && newState === SimpleJobState.Failed) {
                    this.jobFailed.emit()
                }
            }
        } catch {
            // Silently ignore polling errors
        } finally {
            this.fetching = false
        }
    }
}
